import { motion } from 'framer-motion';
import { 
  ArrowLeft, Shield, Award, Users, Globe, Target, 
  CheckCircle, Building, FileText, Phone, Mail, MapPin,
  TrendingUp, Lock, Zap, Heart
} from 'lucide-react';
import { useLanguage } from './i18n';

const COLORS = {
  primary: '#22c55e',
  primaryDark: '#16a34a',
  dark: '#0f172a',
  darkLight: '#1e293b',
  gray: '#64748b',
  grayLight: '#94a3b8',
  white: '#ffffff',
  bg: '#f8fafc'
};

const AboutPage = () => {
  const { t } = useLanguage();

  const stats = [
    { value: '2017', label: 'Год основания' },
    { value: '50 000+', label: 'Активных клиентов' },
    { value: '6', label: 'Поддерживаемых валют' },
    { value: '24/7', label: 'Поддержка клиентов' }
  ];

  const values = [
    {
      icon: <Shield size={28} />,
      title: 'Безопасность',
      description: 'Многоуровневая защита средств и данных клиентов. Шифрование, 2FA, антифрод-системы.'
    },
    {
      icon: <Zap size={28} />,
      title: 'Скорость',
      description: 'Мгновенные операции обмена и переводов. Среднее время транзакции — 5-8 секунд.'
    },
    {
      icon: <Heart size={28} />,
      title: 'Клиентоориентированность',
      description: 'Индивидуальный подход к каждому клиенту. Выгодные курсы и минимальные комиссии.'
    },
    {
      icon: <Globe size={28} />,
      title: 'Доступность',
      description: 'Работаем с клиентами из СНГ и Европы. Поддержка русского и английского языков.'
    }
  ];

  const licenses = [
    {
      number: '№ 159',
      title: 'Лицензия на брокерскую деятельность',
      issuer: 'Государственная служба регулирования и надзора за финансовым рынком КР',
      date: '16.03.2017',
      validity: 'Бессрочная'
    },
    {
      number: '№ 160',
      title: 'Лицензия на дилерскую деятельность',
      issuer: 'Государственная служба регулирования и надзора за финансовым рынком КР',
      date: '16.03.2017',
      validity: 'Бессрочная'
    }
  ];

  const team = [
    {
      role: 'Генеральный директор',
      name: 'Уткина Екатерина Алексеевна',
      description: 'Более 10 лет опыта в финансовой сфере'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/polytech-logo.png" alt="PolyTech Wallet" className="w-10 h-10 rounded-xl" />
            <span className="font-bold text-xl" style={{ color: COLORS.dark }}>PolyTech Wallet</span>
          </a>
          <a 
            href="/"
            className="flex items-center gap-2 text-sm font-medium hover:text-green-600 transition"
            style={{ color: COLORS.gray }}
          >
            <ArrowLeft size={16} />
            На главную
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24" style={{ background: `linear-gradient(135deg, ${COLORS.dark} 0%, ${COLORS.darkLight} 100%)` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 mb-6"
            >
              <Award size={16} style={{ color: COLORS.primary }} />
              <span className="text-sm font-medium" style={{ color: COLORS.primary }}>
                Лицензированный финансовый оператор
              </span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6"
            >
              О компании <span style={{ color: COLORS.primary }}>PolyTech Wallet</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg md:text-xl mb-8" 
              style={{ color: COLORS.grayLight }}
            >
              Мы — команда профессионалов в сфере финансовых технологий, предоставляющая 
              безопасные и удобные решения для обмена валют и международных переводов 
              с 2017 года.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <p className="text-3xl md:text-4xl font-bold mb-2" style={{ color: COLORS.primary }}>{stat.value}</p>
                <p className="text-sm" style={{ color: COLORS.gray }}>{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24" style={{ background: COLORS.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: COLORS.dark }}>
                Наша миссия
              </h2>
              <p className="text-lg mb-6" style={{ color: COLORS.gray }}>
                Сделать международные финансовые операции простыми, быстрыми и доступными 
                для каждого. Мы стремимся устранить барьеры между валютами и странами, 
                предоставляя нашим клиентам инструменты для свободного управления 
                своими финансами.
              </p>
              <div className="space-y-4">
                {[
                  'Прозрачные условия без скрытых комиссий',
                  'Мгновенные переводы в любую точку мира',
                  'Круглосуточная поддержка на русском языке',
                  'Полное соответствие законодательству'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle size={20} style={{ color: COLORS.primary }} />
                    <span style={{ color: COLORS.dark }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div 
                className="aspect-square rounded-3xl flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${COLORS.primary}20 0%, ${COLORS.primaryDark}20 100%)` }}
              >
                <Target size={120} style={{ color: COLORS.primary }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
              Наши ценности
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.gray }}>
              Принципы, которыми мы руководствуемся в работе каждый день
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition"
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
                >
                  {value.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: COLORS.dark }}>{value.title}</h3>
                <p className="text-sm" style={{ color: COLORS.gray }}>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Info Section */}
      <section className="py-16 md:py-24" style={{ background: COLORS.dark }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Юридическая информация
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.grayLight }}>
              Мы работаем в полном соответствии с законодательством
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Company Info */}
            <div className="rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <div className="flex items-center gap-3 mb-6">
                <Building size={24} style={{ color: COLORS.primary }} />
                <h3 className="text-xl font-semibold text-white">Реквизиты компании</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm" style={{ color: COLORS.grayLight }}>Полное наименование</p>
                  <p className="text-white font-medium">ОсОО «ФВА Финанс»</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: COLORS.grayLight }}>Регистрационный номер</p>
                  <p className="text-white font-medium">150744-3301-000</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: COLORS.grayLight }}>ИНН</p>
                  <p className="text-white font-medium">01111201510129</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: COLORS.grayLight }}>ОКПО</p>
                  <p className="text-white font-medium">29284076</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: COLORS.grayLight }}>Юридический адрес</p>
                  <p className="text-white font-medium">720001, Кыргызская Республика, г. Бишкек, Первомайский район, ул. Токтогула, 87A</p>
                </div>
              </div>
            </div>

            {/* Licenses */}
            <div className="rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <div className="flex items-center gap-3 mb-6">
                <Award size={24} style={{ color: COLORS.primary }} />
                <h3 className="text-xl font-semibold text-white">Лицензии</h3>
              </div>
              <div className="space-y-6">
                {licenses.map((license, i) => (
                  <div key={i} className="pb-4 border-b border-gray-700 last:border-0 last:pb-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 rounded text-xs font-medium" style={{ background: COLORS.primary, color: 'white' }}>
                        {license.number}
                      </span>
                      <span className="text-xs" style={{ color: COLORS.grayLight }}>{license.validity}</span>
                    </div>
                    <p className="text-white font-medium mb-1">{license.title}</p>
                    <p className="text-sm" style={{ color: COLORS.grayLight }}>
                      Выдана: {license.issuer}
                    </p>
                    <p className="text-sm" style={{ color: COLORS.grayLight }}>
                      Дата выдачи: {license.date}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center">
            <a 
              href="/documents"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition hover:opacity-90"
              style={{ background: COLORS.primary, color: 'white' }}
            >
              <FileText size={20} />
              Все документы
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
              Свяжитесь с нами
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.gray }}>
              Мы всегда рады ответить на ваши вопросы
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl" style={{ background: COLORS.bg }}>
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
              >
                <Phone size={28} />
              </div>
              <h3 className="font-semibold mb-2" style={{ color: COLORS.dark }}>Телефон</h3>
              <a href="tel:+996507210992" className="hover:underline" style={{ color: COLORS.primary }}>
                +996 507 210 992
              </a>
            </div>

            <div className="text-center p-8 rounded-2xl" style={{ background: COLORS.bg }}>
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
              >
                <Mail size={28} />
              </div>
              <h3 className="font-semibold mb-2" style={{ color: COLORS.dark }}>Email</h3>
              <a href="mailto:support@polytechex.com" className="hover:underline" style={{ color: COLORS.primary }}>
                support@polytechex.com
              </a>
            </div>

            <div className="text-center p-8 rounded-2xl" style={{ background: COLORS.bg }}>
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
              >
                <MapPin size={28} />
              </div>
              <h3 className="font-semibold mb-2" style={{ color: COLORS.dark }}>Адрес</h3>
              <p style={{ color: COLORS.gray }}>
                г. Бишкек, ул. Токтогула, 87A
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm" style={{ color: COLORS.gray }}>
            © 2017-2026 ОсОО «ФВА Финанс». Все права защищены.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
